import queryString from 'query-string';
import { ExecuteAll } from 'grb-webui/services/Promise';
import { sameValueArrays } from 'grb-webui/services/diff';
import {
  UPLOADER_READY_STATUS,
  UPLOADER_UPLOAD_STATUS,
  UPLOADER_ERROR_STATUS,
  UPLOADER_ABORTED_STATUS,
  UPLOADER_DONE_STATUS,
} from './Uploader';
import { getFileExtensions, MODEL_EXTENSIONS } from './BatchFileValidator';
import { BATCHES_ENTITY } from '../Entities';
import BatchUploader from './BatchUploader';

const BATCH_STATUS_UNKOWN = 0;
const BATCH_STATUS_CREATED = 1;
const BATCH_STATUS_SUBMITTED = 2;
const BATCH_STATUS_ABORTED = 3;
const BATCH_STATUS_FAILED = 4;
const BATCH_STATUS_COMPLETED = 5;

// Batch actions, must match action ids in "permissions" node of the app JSON config file.
const ACTION_RETRY_BATCH = 'retryBatch';
const ACTION_ABORT_BATCH = 'abortBatch';
const ACTION_DISCARD_BATCH = 'discardBatch';
const ACTION_START_BATCH = 'startBatch';
const ACTION_STOP_BATCH = 'stopBatch';

const getBatchJobId = (batch) => {
  return batch.jobs && batch.jobs.length ? batch.jobs[batch.jobs.length - 1].jobId : undefined;
};

class BatchService {
  constructor(config, store, options) {
    this.apiClient = config.apiClient;
    this.apiClientPrefix = config.apiClientPrefix;
    this.authService = config.authService;

    const { uri = '/batches', type = 'Batch' } = options;
    this.uri = uri;
    this.store = store;

    // Register computed properties
    const { schemaService } = config;
    schemaService.addComputedProperty({
      name: 'batchJob',
      typename: type,
      label: 'Job',
      type: 'string',
      compute: getBatchJobId,
    });

    schemaService.addComputedProperty({
      name: 'batchModel',
      typename: type,
      label: 'Model',
      type: 'string',
      compute: (batch) => {
        const modelFile = (batch.input || []).find((file) => {
          const [, ext] = getFileExtensions(file.name);
          return MODEL_EXTENSIONS.indexOf(ext) >= 0;
        });
        return modelFile ? modelFile.name : undefined;
      },
    });

    schemaService.addComputedProperty({
      name: 'batchDuration',
      typename: type,
      label: 'Duration',
      type: 'string',
      compute: (batch) => {
        if (!batch.submittedAt) return undefined;
        const end = batch.endedAt || new Date().getTime();
        return (end - batch.submittedAt) / 1000;
      },
    });

    schemaService.addComputedProperty({
      name: 'batchSize',
      typename: type,
      label: 'Duration',
      type: 'string',
      compute: (batch) => {
        if (batch.discarded) return 0;
        let size = (batch.input || []).reduce((acc, file) => {
          return acc + (file.length || 0);
        }, 0);
        size += (batch.jobs || []).reduce((acc, job) => {
          return acc + (job.output && job.output.length && job.output.reduce((osize, o) => { return osize + (o.length || 0); }, 0) || 0);
        }, 0);
        return size;
      },
    });

    const defaultPolling = options && options.polling && options.polling.defaultDelay;

    // Register the batches entity
    store.addEntity(BATCHES_ENTITY, {
      request: context => this.requestBatches(context.query),
      type: config.schemaService.getType(type),
      polling: defaultPolling,
      defaultValue: [],
      computeDiffs: true,
    });

    this.abortingBatches = {};
    this.retryingBatches = {};
    this.batchLogs = {};
    this.batchFilter = undefined;
    this.batchCreateUploader = new BatchUploader({ apiClient: config.apiClient, batchService: this });
  }

  getBatchName(job) {
    return job.id;
  }

  getBatches() {
    return this.batchesPolling.data;
  }

  validateAbort(batch) {
    const { userId } = batch;
    if (this.abortingBatches[batch.id]) {
      return `Batch ${this.getBatchName(batch)} is already aborting`;
    }
    const authResponse = this.authService.canPerformAction(ACTION_ABORT_BATCH, batch, userId);
    if ((authResponse !== true) && (authResponse !== null)) {
      return authResponse;
    }
    if (this.isBatchFinished(batch)) {
      return `Batch ${this.getBatchName(batch)} must not be finished to be aborted`;
    }
    return null;
  }

  canAbort(batch) {
    return !this.isBatchFinished(batch);
  }

  abortBatch(batch) {
    this.abortingBatches[batch.id] = true;
    return this.requestAbortBatch(batch.id)
      .finally(() => { delete this.abortingBatches[batch.id]; });
  }

  requestAbortBatch(batchId) {
    return this.apiClient.delete(`${this.uri}/${batchId}/processing`);
  }

  printError(error) {
    // Process error if it contains an error object, generated by the optimization server
    if (!error.response || !error.response.data || !error.response.data.error) {
      return error.message || error;
    }
    const processedError = Object.assign({}, error.response.data.error);
    if (processedError.code) {
      return `[${processedError.code}] ${processedError.message}`;
    }
    return processedError.message;
  }

  abortBatchs(batches) {
    const authResponse = batches
      .map(batch => this.authService.canPerformAction(ACTION_ABORT_BATCH, batch, batch.userId))
      .find(result => result !== true);
    if (authResponse !== undefined) {
      const error = new Error(
        authResponse === false ? 'Current user does not have permissions to abort the selected batches' : authResponse,
      );
      error.severity = 'info';
      return Promise.reject(error);
    }
    const batchErrors = {};
    return Promise.all(
      batches.map(batch => this.abortBatch(batch).catch((err) => { batchErrors[batch.id] = err; })),
    ).then(() => {
      const failedBatchIds = Object.keys(batchErrors);
      if (failedBatchIds.length) {
        return Promise.reject(new Error(failedBatchIds.map(id => this.printError(batchErrors[id]).join('\n'))));
      }
      return true;
    });
  }

  validateRetry(batch) {
    const { userId } = batch;
    if (this.retryingBatches[batch.id]) {
      return `Batch ${this.getBatchName(batch)} is already retrying`;
    }
    const authResponse = this.authService.canPerformAction(ACTION_RETRY_BATCH, batch, userId);
    if (authResponse !== true) {
      return authResponse;
    }
    if (batch.status !== BATCH_STATUS_FAILED && batch.status !== BATCH_STATUS_ABORTED) {
      return `Batch ${this.getBatchName(batch)} must be failed or aborted to be retried`;
    }
    return null;
  }

  retryBatch(batch) {
    this.retryingBatches[batch.id] = true;
    return this.requestRetryBatch(batch.id)
      .finally(() => { delete this.retryingBatches[batch.id]; });
  }

  requestRetryBatch(batchId) {
    return this.apiClient.post(`${this.uri}/${batchId}/processing?retry=true`);
  }

  hasLog(batch) {
    return this.isBatchFinished(batch);
  }

  subscribeBatchLog(batch, subscriber) {
    const { id } = batch;
    let node = this.batchLogs[id];
    if (!node) {
      node = {
        first: true,
        status: {
          full: false,
          p: undefined,
          log: '',
        },
        subscribers: [subscriber],
        stop: () => {
          if (node.timeOutId) {
            clearTimeout(node.timeOutId);
            delete node.timeOutId;
          }
        },
        update: () => {
          const params = {};
          if (node.first) {
            if (node.status.full) {
              params.b = true;
            } else {
              params.n = 50;
            }
          } else {
            params.p = node.status.index;
          }
          this.apiClient.get(`/batches/${id}/log`, { params }).then((res) => {
            node.status = Object.assign({}, node.status, {
              log: node.first ? res.data : (node.status.log + res.data),
              index: res.headers['x-gurobi-index'],
            });
            node.first = false;
            node.subscribers.forEach(s => s(node.status));
            if (node.status.index !== '-1') {
              node.timeOutId = setTimeout(node.update, 1000);
            } else {
              delete node.timeOutId;
            }
          }).catch(this.onError);
        },
      };
      this.batchLogs[id] = node;
      node.update();
    } else {
      node.subscribers.push(subscriber);
    }
    return node.status;
  }

  requestBatchLog(batchId, params) {
    return this.apiClient.get(`${this.uri}/${batchId}/log`, { params });
  }

  unsubscribeBatchLog(batch, subscriber) {
    const { id } = batch;
    const node = this.batchLogs[id];
    if (node) {
      const index = node.subscribers.indexOf(subscriber);
      if (index >= 0) {
        node.subscribers.splice(index, 1);
        if (node.subscribers.length === 0) {
          this.batchLogs[id].stop();
          delete this.batchLogs[id];
        }
      }
    }
  }

  fetchFullLog(batch) {
    const { id } = batch;
    const node = this.batchLogs[id];
    if (node) {
      node.stop();
      node.first = true;
      node.status = Object.assign({}, node.status, { full: true });
      node.update();
    }
  }

  validateDiscardBatch(batch) {
    const { userId } = batch;
    const authResponse = this.authService.canPerformAction(ACTION_DISCARD_BATCH, batch, userId) || null;
    return authResponse !== true
        ? authResponse
        : (batch.discarded && `Batch ${batch.id} is already discarded` || null);
  }

  deleteBatch(batchId) {
    return this.requestDeleteBatch(batchId);
  }

  requestDeleteBatch(batchId) {
    return this.apiClient.delete(`${this.uri}/${batchId}`);
  }

  deleteBatches(batches) {
    return Promise.all(
      batches.map(batch => this.requestDeleteBatch(batch.id)),
    );
  }

  discardBatch(batchId) {
    return this.requestDiscardBatch(batchId);
  }

  requestDiscardBatch(batchId) {
    return this.apiClient.delete(`${this.uri}/${batchId}/content`);
  }

  discardBatches(batches) {
    return ExecuteAll(
      batches.map(batch => this.requestDiscardBatch(batch.id)),
    );
  }

  getBatch(id) {
    return this.batchesPolling.data.find(batch => batch.id === id);
  }

  isBatchFinished(batch) {
    return batch.status === BATCH_STATUS_ABORTED
          || batch.status === BATCH_STATUS_COMPLETED
          || batch.status === BATCH_STATUS_FAILED;
  }

  /**
   * Deprecated, only used for dashboar module
   * @param {*} batchFilter the batch filter
   */
  fetchBatches(batchFilter) {
    const convertTimeRangeToQueryParams = (timerange, queryParam) => {
      if (timerange) {
        const { from, to } = timerange;
        if (from) {
          const presets = ['24h', '7d', '30d'];
          if (presets.indexOf(from) >= 0) {
            queryParam.filter = from;
          } else if (from) {
            queryParam.from = from.getTime();
            queryParam.to = to === 'today' ? new Date().getTime() : to.getTime();
          }
        }
      }
    };
    // Convert this.batchFilter into query parameters
    const filter = batchFilter || {};
    const queryParam = {
      length: filter.batchLimit,
      status: filter.batchstatus,
      userId: filter.userId,
      batchId: filter.batchId,
      app: filter.app,
    };
    if (filter.username) {
      queryParam.username = (filter.username === '[Current]') && this.authService.getUsername()
            || filter.username;
    }
    if (filter.discarded !== undefined) {
      queryParam.discarded = filter.discarded;
    }
    convertTimeRangeToQueryParams(filter.timerange, queryParam);
    const query = `?${queryString.stringify(queryParam)}`;
    return this.requestBatches(query, queryParam);
  }

  requestBatches(query) {
    return this.apiClient.get(`${this.uri}${query || ''}`);
  }

  restartBatchesPolling() {
    return this.store.startPolling(BATCHES_ENTITY); // start() checks if already started
  }

  stopBatchesPolling() {
    this.batchesPolling.stop();
  }

  subscribeBatches(subscriber) {
    this.batchesSubs.push(subscriber);
    const batches = this.batchesPolling.getData() || [];
    subscriber(batches, { newObjects: batches });
  }

  unsubscribeBatches(subscriber) {
    const index = this.batchesSubs.indexOf(subscriber);
    if (index >= 0) {
      this.batchesSubs.splice(index, 1);
    }
  }

  sameBatchFilter(params) {
    if (this.batchFilter === params) return true;
    if (!this.batchFilter || !params) return false;
    const sameValues = (v1, v2) => {
      if (!v1) return !v2;
      return v1 === v2;
    };
    if (['batchLimit', 'username', 'batchId', 'app'].find(name => !sameValues(params[name], this.batchFilter[name]))) {
      return false;
    }
    if (this.batchFilter.discarded !== params.discarded) {
      return false;
    }
    if (!sameValueArrays(this.batchFilter.batchstatus, params.batchstatus)) {
      return false;
    }
    const { timerange: thisTimeRange = {} } = this.batchFilter;
    const { timerange: paramTimeRange = {} } = params;
    return sameValues(thisTimeRange.from, paramTimeRange.from)
              && sameValues(thisTimeRange.to, paramTimeRange.to);
  }

  isStartBatchAllowed() {
    return this.authService.canPerformAction(ACTION_START_BATCH) === true;
  }

  isStopBatchAllowed() {
    return this.authService.canPerformAction(ACTION_STOP_BATCH) === true;
  }

  setBatchConfig(id, config) {
    return this.requestBatchConfig(id, config)
      .finally(() => {
        this.restartBatchesPolling();
      });
  }

  requestBatchConfig(batchId, config) {
    return this.apiClient.post(`${this.uri}/${batchId}/config`, config);
  }

  createBatch(batchCreate) {
    return this.requestCreateBatch(batchCreate).then((res) => {
      const url = res.headers.location;
      const index = url.lastIndexOf('/');
      const id = index >= 0 ? url.substring(index + 1) : url;
      return id;
    });
  }

  requestCreateBatch(batchCreate) {
    return this.apiClient.post(this.uri, Object.assign({}, batchCreate, {
      pid: 0,
      apitype: 'grbrsm_ui',
    }));
  }

  attachObjectToBatch(batchId, storageObject) {
    return this.requestAttachObjectToBatch(batchId, storageObject);
  }

  requestAttachObjectToBatch(batchId, storageObject) {
    return this.apiClient.post(`${this.uri}/${batchId}/objects`, storageObject);
  }

  closeBatch(batchId) {
    return this.requestCloseBatch(batchId);
  }

  requestCloseBatch(batchId) {
    return this.apiClient.post(`${this.uri}/${batchId}/processing`);
  }

  getBatchJSonSolutionOutput(batch) {
    const output = batch.jobs && batch.jobs.length && batch.jobs[batch.jobs.length - 1].output || [];
    return output.find(f => f.name.indexOf('solution.json') >= 0);
  }

  getBatchCreateUploader() {
    return this.batchCreateUploader;
  }

  destroy() {
    this.batchesPolling.stop();
    this.batchesPolling.unsubscribe(this.batchPollingHandle);
    if (this.batchRoute) {
      this.batchRoute.unsubscribeContextChange(this.contextSub);
    }
  }

  // Errors
  setErrorHandler(onError) {
    this.onError = onError;
  }
}

export {
  BatchService as default,
  BATCH_STATUS_UNKOWN,
  BATCH_STATUS_CREATED,
  BATCH_STATUS_SUBMITTED,
  BATCH_STATUS_ABORTED,
  BATCH_STATUS_FAILED,
  BATCH_STATUS_COMPLETED,
  UPLOADER_READY_STATUS,
  UPLOADER_UPLOAD_STATUS,
  UPLOADER_ERROR_STATUS,
  UPLOADER_ABORTED_STATUS,
  UPLOADER_DONE_STATUS,
  getBatchJobId,
};
